/**
 * Landing Config Service - Manages landing page configuration in Firestore
 */
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { LandingConfig, LandingFeature } from '@/types';

const LANDING_CONFIG_DOC = 'config/landing';

/**
 * Default landing configuration
 */
const DEFAULT_CONFIG: LandingConfig = {
  schedule: {
    weekdays: '10:00 - 21:00',
    saturday: '10:00 - 20:00',
    sunday: 'Cerrado'
  },
  averageWaitTime: '15min',
  features: [
    {
      icon: '🐵',
      titleKey: 'home.features.fast.title',
      descriptionKey: 'home.features.fast.description',
      order: 1
    },
    {
      icon: '🐒',
      titleKey: 'home.features.reliable.title',
      descriptionKey: 'home.features.reliable.description',
      order: 2
    },
    {
      icon: '🙈',
      titleKey: 'home.features.precise.title',
      descriptionKey: 'home.features.precise.description',
      order: 3
    },
    {
      icon: '🔒',
      titleKey: 'home.features.secure.title',
      descriptionKey: 'home.features.secure.description',
      order: 4
    }
  ],
  updatedAt: Timestamp.now()
};

/**
 * Get landing configuration from Firestore
 * Returns default config if none exists
 */
export async function getLandingConfig(): Promise<LandingConfig> {
  try {
    const docRef = doc(db, LANDING_CONFIG_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        schedule: data.schedule || DEFAULT_CONFIG.schedule,
        averageWaitTime: data.averageWaitTime || DEFAULT_CONFIG.averageWaitTime,
        features: (data.features || DEFAULT_CONFIG.features).sort((a: LandingFeature, b: LandingFeature) => a.order - b.order),
        updatedAt: data.updatedAt || Timestamp.now()
      };
    }

    // If no config exists, create default one
    await setDoc(docRef, DEFAULT_CONFIG);
    return DEFAULT_CONFIG;
  } catch (error) {
    console.error('Error getting landing config:', error);
    // Return default config on error
    return DEFAULT_CONFIG;
  }
}

/**
 * Update landing configuration in Firestore
 */
export async function updateLandingConfig(config: Partial<LandingConfig>): Promise<void> {
  try {
    const docRef = doc(db, LANDING_CONFIG_DOC);

    // Get current config
    const current = await getLandingConfig();

    // Merge with updates
    const updated: LandingConfig = {
      ...current,
      ...config,
      updatedAt: Timestamp.now()
    };

    // Sort features by order
    if (updated.features) {
      updated.features = updated.features.sort((a, b) => a.order - b.order);
    }

    await setDoc(docRef, updated);
  } catch (error) {
    console.error('Error updating landing config:', error);
    throw new Error('Failed to update landing configuration');
  }
}

/**
 * Initialize landing configuration with default values
 * Only creates if document doesn't exist
 */
export async function initializeLandingConfig(): Promise<void> {
  try {
    const docRef = doc(db, LANDING_CONFIG_DOC);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      await setDoc(docRef, DEFAULT_CONFIG);
      console.log('Landing config initialized with default values');
    }
  } catch (error) {
    console.error('Error initializing landing config:', error);
    throw new Error('Failed to initialize landing configuration');
  }
}
